import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { imgUrl, imgSrcSet, money, localWebp } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export const ProductCard = ({ product, index = 0, priority = false }) => {
  const { isWishlisted, toggleWishlist } = useStore();
  const [hovered, setHovered] = useState(false);
  const wishlisted = isWishlisted(product.id);
  const hasAlt = product.images.length > 1;

  const currentImg = hovered && hasAlt ? product.images[1] : product.images[0];
  const isLocal = /^\/products\//.test(currentImg);
  const src = isLocal ? localWebp(currentImg, 800) : imgUrl(currentImg, 700);
  const { srcSet, sizes } = imgSrcSet(currentImg);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      className="group relative"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div
          className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <img
            src={src}
            srcSet={srcSet}
            sizes={sizes}
            alt={product.name}
            width="700"
            height="875"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {product.compare_at_price && (
            <span className="absolute top-3 left-3 bg-[#6D28D9] text-white text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
              Sale
            </span>
          )}
          {product.limited && !product.compare_at_price && (
            <span className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-[#C9A24B]/50 text-[#C9A24B] text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
              Limited
            </span>
          )}
          {product.featured && !product.compare_at_price && !product.limited && (
            <span className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-[#0FB5A3]/40 text-[#0FB5A3] text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
              Signature
            </span>
          )}
        </div>
      </Link>
      <button
        data-testid={`wishlist-toggle-${product.slug}`}
        aria-label="Toggle wishlist"
        onClick={() => toggleWishlist(product)}
        className={`absolute top-3 right-3 p-2 backdrop-blur bg-black/50 transition-colors ${
          wishlisted ? "text-[#0FB5A3]" : "text-white/60 hover:text-white"
        }`}
      >
        <Heart size={16} strokeWidth={1.5} fill={wishlisted ? "#0FB5A3" : "none"} />
      </button>
      <div className="pt-4 flex items-start justify-between gap-3">
        <div>
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-display text-sm text-white tracking-wide group-hover:text-[#0FB5A3] transition-colors">
              {product.name}
            </h3>
          </Link>
          <p className="text-[10px] text-white/35 uppercase tracking-[0.25em] mt-1">
            {product.collection} · {product.category}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm text-white" data-testid={`product-price-${product.slug}`}>{money(product.price)}</p>
          {product.compare_at_price && (
            <p className="text-xs text-white/30 line-through">{money(product.compare_at_price)}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};
